import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
} from '@mui/material';
import { Role, Permission, usersService, CreateRoleRequest, UpdateRoleRequest } from '../../services/usersService';

interface RoleFormProps {
  role?: Role;
  permissions: Permission[];
  onSubmit: () => void;
  onCancel: () => void;
  isEdit?: boolean;
}

const RoleForm: React.FC<RoleFormProps> = ({
  role,
  permissions,
  onSubmit,
  onCancel,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    name: role?.name || '',
    description: role?.description || '',
    permission_ids: role?.permissions.map(p => p.id) || [],
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handlePermissionChange = (permissionId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: checked
        ? [...prev.permission_ids, permissionId]
        : prev.permission_ids.filter(id => id !== permissionId),
    }));
  };

  const handleSelectAllPermissions = () => {
    const allPermissionIds = permissions.map(p => p.id);
    const allSelected = allPermissionIds.every(id => formData.permission_ids.includes(id));
    
    setFormData(prev => ({
      ...prev,
      permission_ids: allSelected ? [] : allPermissionIds,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('El nombre del rol es requerido');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      if (isEdit && role) {
        // Update role
        const updateData: UpdateRoleRequest = {
          name: formData.name,
          description: formData.description || undefined,
          permission_ids: formData.permission_ids,
        };
        
        await usersService.updateRole(role.id, updateData);
      } else {
        // Create role
        const createData: CreateRoleRequest = {
          name: formData.name,
          description: formData.description || undefined,
          permission_ids: formData.permission_ids,
        };
        
        await usersService.createRole(createData);
      }
      
      onSubmit();
    } catch (err: any) {
      console.error('Form submission error:', err);
      
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError(isEdit ? 'Error al actualizar rol' : 'Error al crear rol');
      }
    } finally {
      setLoading(false);
    }
  };

  const groupedPermissions = permissions.reduce((groups, permission) => {
    // Group permissions by their prefix (e.g., 'view_', 'manage_', etc.)
    const prefix = permission.name.split('_')[0];
    if (!groups[prefix]) {
      groups[prefix] = [];
    }
    groups[prefix].push(permission);
    return groups;
  }, {} as Record<string, Permission[]>);

  const getGroupTitle = (prefix: string): string => {
    const titles: Record<string, string> = {
      'view': 'Visualización',
      'manage': 'Gestión',
      'configure': 'Configuración',
      'export': 'Exportación',
      'system': 'Sistema',
    };
    return titles[prefix] || prefix.charAt(0).toUpperCase() + prefix.slice(1);
  };

  const allPermissionIds = permissions.map(p => p.id);
  const allSelected = allPermissionIds.every(id => formData.permission_ids.includes(id));
  const someSelected = formData.permission_ids.length > 0 && !allSelected;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <TextField
        name="name"
        label="Nombre del Rol"
        value={formData.name}
        onChange={handleInputChange}
        fullWidth
        required
        margin="normal"
        disabled={loading}
      />
      
      <TextField
        name="description"
        label="Descripción"
        value={formData.description}
        onChange={handleInputChange}
        fullWidth
        multiline
        rows={3}
        margin="normal"
        disabled={loading}
        placeholder="Descripción opcional del rol..."
      />

      <Box sx={{ mt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Permisos ({formData.permission_ids.length} seleccionados)
          </Typography>
          <Button
            onClick={handleSelectAllPermissions}
            size="small"
            disabled={loading}
          >
            {allSelected ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
          </Button>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
          {Object.entries(groupedPermissions).map(([prefix, groupPermissions]) => (
            <Box key={prefix} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                {getGroupTitle(prefix)}
              </Typography>
              <FormGroup>
                {(groupPermissions as Permission[]).map((permission: Permission) => (
                  <FormControlLabel
                    key={permission.id}
                    control={
                      <Checkbox
                        checked={formData.permission_ids.includes(permission.id)}
                        onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">
                          {permission.name}
                        </Typography>
                        {permission.description && (
                          <Typography variant="caption" color="text.secondary">
                            {permission.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
              {Object.keys(groupedPermissions).indexOf(prefix) < Object.keys(groupedPermissions).length - 1 && (
                <Divider sx={{ mt: 1 }} />
              )}
            </Box>
          ))}
        </Paper>
      </Box>
      
      <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          className="arquitectura-gradient"
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            isEdit ? 'Actualizar' : 'Crear'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default RoleForm;